import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Box, Column, FullBox, PageContentHeader, Row, Spinner, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { ActivityLogFilters } from '~/components/ActivityLogFilters/ActivityLogFilters';
import ActivityLogItem from '~/components/ActivityLogItem/ActivityLogItem';
import { ActivityLogSearch } from '~/components/ActivityLogSearch/ActivityLogSearch';
import { ActivityLogShowNotificationsToggle } from '~/components/ActivityLogShowNotificationsToggle/ActivityLogShowNotificationsToggle';
import { useStore } from '~/contexts';
import { useActivityLog } from '~/hooks/reactQuery/queries/useActivityLog';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import styles from './ActivityLogContainer.module.scss';
import { filterEntries } from './filterEntries';
import { useActivityLogPreferences } from './hooks/useActivityLogPreferences';
import { useUpdateActivityLogPreferences } from './hooks/useUpdateActivityLogPreferences';
import { useActivityLogUrlState } from './useActivityLogUrlState';
export const GetActivityLogContainerTID = (parentTestId)=>getTestIds(parentTestId);
export const ActivityLogContainer = (param)=>{
    let { relationship, surface, defaultShowNotifications = true, testId } = param;
    var _ref, _relationshipModel_sellerFullName, _relationshipModel_buyerFullName;
    const testIds = useMemo(()=>GetActivityLogContainerTID(testId).ActivityLogContainerTestIds, [
        testId
    ]);
    const { t } = useLocaleKeys();
    const userId = useStore(selectors.user.id);
    const isClient = relationshipModel.isBuyer(relationship, userId);
    const { preferences } = useActivityLogPreferences();
    const { save } = useUpdateActivityLogPreferences();
    const showNotifications = (_ref = preferences === null || preferences === void 0 ? void 0 : preferences.showSentNotifications) !== null && _ref !== void 0 ? _ref : defaultShowNotifications;
    const { searchCommitted, appliedTypes, setSearchCommitted, setAppliedTypes } = useActivityLogUrlState();
    const handleShowNotificationsChange = useCallback((next)=>{
        var _ref;
        save({
            showSentNotifications: next,
            updatedAt: (_ref = preferences === null || preferences === void 0 ? void 0 : preferences.updatedAt) !== null && _ref !== void 0 ? _ref : null
        });
    }, [
        save,
        preferences === null || preferences === void 0 ? void 0 : preferences.updatedAt
    ]);
    const { activityLog, isLoading, error } = useActivityLog(relationship.id, {
        surface
    });
    const filteredEntries = useMemo(()=>filterEntries(activityLog, {
            searchCommitted,
            appliedTypes,
            showNotifications
        }), [
        activityLog,
        searchCommitted,
        appliedTypes,
        showNotifications
    ]);
    const toolbar = /*#__PURE__*/ _jsx(PageContentHeader, {
        children: /*#__PURE__*/ _jsxs(Row, {
            testId: testIds.Toolbar(),
            alignItems: "center",
            justifyContent: "space-between",
            className: styles.toolbar,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 12,
                    children: [
                        /*#__PURE__*/ _jsx(ActivityLogSearch, {
                            onChange: setSearchCommitted,
                            value: searchCommitted,
                            testId: testIds.Search()
                        }),
                        /*#__PURE__*/ _jsx(ActivityLogFilters, {
                            appliedSelection: appliedTypes,
                            onApply: setAppliedTypes,
                            isClient: isClient,
                            testId: testIds.Filters()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ActivityLogShowNotificationsToggle, {
                    value: showNotifications,
                    onChange: handleShowNotificationsChange,
                    testId: testIds.Toggle()
                })
            ]
        })
    });
    if (error) {
        return /*#__PURE__*/ _jsxs(FullBox, {
            testId: testIds.Root(),
            children: [
                toolbar,
                /*#__PURE__*/ _jsx(Box, {
                    testId: testIds.Error(),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: t.pages.agreementMinisite.tryAgain()
                    })
                })
            ]
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(FullBox, {
            testId: testIds.Root(),
            children: [
                toolbar,
                /*#__PURE__*/ _jsx(FullBox, {
                    testId: testIds.Spinner(),
                    children: /*#__PURE__*/ _jsx(Spinner, {
                        centeredInBox: true,
                        centerBoxHeight: 200
                    })
                })
            ]
        });
    }
    const vendorName = (_relationshipModel_sellerFullName = relationshipModel.sellerFullName(relationship)) !== null && _relationshipModel_sellerFullName !== void 0 ? _relationshipModel_sellerFullName : '';
    const clientName = (_relationshipModel_buyerFullName = relationshipModel.buyerFullName(relationship)) !== null && _relationshipModel_buyerFullName !== void 0 ? _relationshipModel_buyerFullName : '';
    return /*#__PURE__*/ _jsxs(FullBox, {
        testId: testIds.Root(),
        children: [
            toolbar,
            filteredEntries.length === 0 ? /*#__PURE__*/ _jsxs(Box, {
                testId: testIds.Empty(),
                className: styles.empty,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        weight: "bold",
                        children: t.activityLog.emptyFilteredTitle()
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "secondary",
                        children: t.activityLog.emptyFilteredSubtitle()
                    })
                ]
            }) : /*#__PURE__*/ _jsx(Column, {
                testId: testIds.Entries(),
                children: filteredEntries.map((entry, idx)=>/*#__PURE__*/ _jsx(ActivityLogItem, {
                        entry: entry,
                        isFirst: idx === 0,
                        isLast: idx === filteredEntries.length - 1,
                        vendorName: vendorName,
                        clientName: clientName,
                        relationship: relationship
                    }, entry.id()))
            })
        ]
    });
};
